import React, { useCallback } from 'react';
import { useSend } from '@sc1/common/hooks';
import { schedule, ScionTimelock } from '@sc1/common';
import { Card, Row, Heading, Text } from '../core';
import { StatusButton } from '../StatusButton';
import { ScheduleIcon } from '../icons';
import type { CardProps } from 'theme-ui';
import { Contract } from 'ethers';

interface SimpleMethodProps<T, F> extends CardProps {
  contract: T | undefined;
  timelock?: ScionTimelock;
  method: F;
  label: string;
  disabled: boolean;
  hint?: string;
  labelSubmit?: string;
}
export const SimpleMethod = <T extends Contract, F extends keyof T & string>({
  contract,
  timelock,
  method,
  label,
  disabled,
  hint,
  labelSubmit,
  ...rest
}: SimpleMethodProps<T, F>): JSX.Element => {
  const { sendTx, txStatus } = useSend();

  const handleSubmit = useCallback(() => {
    if (!contract) return;
    const params = [] as unknown[] as Parameters<T[F]>;
    if (timelock) {
      return schedule(sendTx, timelock, contract, method);
    }
    sendTx(contract, method, params);
  }, [contract, timelock, method, schedule, sendTx]);

  return (
    <Card {...rest}>
      {hint && (
        <Text variant="hint" mb={1}>
          {hint}
        </Text>
      )}
      <Heading as="h3" mb={2}>
        {label}
      </Heading>
      <Row variant="collapse">
        <StatusButton
          disabled={disabled}
          onClick={handleSubmit}
          status={txStatus}
          suffix={timelock ? <ScheduleIcon /> : null}
        >
          {labelSubmit || 'Submit'}
        </StatusButton>
      </Row>
    </Card>
  );
};
